import React from 'react'
import {
  Plus,
  Trash2,
  Rows,
  LayoutList,
  Type,
  ThumbsUp,
  ThumbsDown,
  Circle,
  ArrowUp,
  ArrowDown,
  Minus,
  TextCursorInput
} from 'lucide-react'
import { motion } from 'framer-motion'

export default function AdminBuilder({
  questions,
  addQuestion,
  updateQuestion,
  deleteQuestion,
  addOption,
  updateOption,
  deleteOption,
  reorderQuestions
}) {
  const iconOrder = ['none', 'thumbsUp', 'thumbsDown', 'circle', 'minus']

  const getNextIcon = current => {
    const index = iconOrder.indexOf(current || 'none')
    const nextIndex = (index + 1) % iconOrder.length
    return iconOrder[nextIndex]
  }

  const renderIcon = icon => {
    if (!icon || icon === 'none')
      return <Minus size={16} className="opacity-30 text-gray-400" />

    if (icon === 'thumbsUp')
      return <ThumbsUp size={16} className="text-green-600" />

    if (icon === 'thumbsDown')
      return <ThumbsDown size={16} className="text-red-600" />

    if (icon === 'minus')
      return <Minus size={16} className="text-red-600" />

    if (icon === 'circle')
      return <Circle size={16} className="text-gray-700" />
  }

  const moveQuestion = (index, direction) => {
    const newQuestions = [...questions]
    const targetIndex = direction === 'up' ? index - 1 : index + 1
    if (targetIndex < 0 || targetIndex >= newQuestions.length) return
    const temp = newQuestions[index]
    newQuestions[index] = newQuestions[targetIndex]
    newQuestions[targetIndex] = temp
    reorderQuestions(newQuestions)
  }

  return (
    <div className="rounded-3xl border-4 border-orange-500 bg-white p-8">
      <h2 className="mb-8 text-xl font-black text-orange-600">
        Admin POV
      </h2>

      <button
        onClick={addQuestion}
        className="mb-10 flex items-center gap-2 rounded-2xl border-4 border-orange-500 px-5 py-2 font-bold text-orange-600 transition active:scale-95"
      >
        <Plus size={18} /> Add Question
      </button>

      {questions.map((q, index) => {
        const isOnly = questions.length === 1
        const isFirst = index === 0
        const isLast = index === questions.length - 1

        const disableUp = isOnly || isFirst
        const disableDown = isOnly || isLast

        const conditionalParents = questions.filter(
          parent => parent.id !== q.id && parent.type === 'vote'
        )

        const voteLayout = q.layout || 'stack'
        const size = q.size || 'regular'

        return (
          <motion.div
            key={q.id}
            layout
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-10 rounded-3xl border-4 border-orange-300 p-6 bg-gray-50"
          >
            {/* REORDER */}
            <div className="mb-4 flex justify-end gap-2">
              <button
                disabled={disableUp}
                onClick={() => moveQuestion(index, 'up')}
                className={`flex h-9 w-9 items-center justify-center rounded-xl border-4 ${
                  disableUp
                    ? 'border-orange-200 text-orange-200 opacity-40'
                    : 'border-orange-300 text-orange-600'
                }`}
              >
                <ArrowUp size={16} />
              </button>

              <button
                disabled={disableDown}
                onClick={() => moveQuestion(index, 'down')}
                className={`flex h-9 w-9 items-center justify-center rounded-xl border-4 ${
                  disableDown
                    ? 'border-orange-200 text-orange-200 opacity-40'
                    : 'border-orange-300 text-orange-600'
                }`}
              >
                <ArrowDown size={16} />
              </button>
            </div>

            <input
              value={q.title}
              onChange={e => updateQuestion(q.id, { title: e.target.value })}
              className="mb-6 w-full rounded-2xl border-4 border-orange-200 px-4 py-3 text-lg font-semibold focus:outline-none"
            />

            {/* REQUIRED + CONDITIONAL */}
            <div className="mb-6 flex flex-wrap items-center gap-6">
              {/* REQUIRED */}
              <div className="flex items-center gap-4">
                <span className="font-bold text-orange-700">
                  Required
                </span>

                <button
                  onClick={() =>
                    updateQuestion(q.id, {
                      required: !q.required
                    })
                  }
                  className={`relative h-9 w-20 rounded-full border-4 transition ${
                    q.required
                      ? 'border-orange-500 bg-orange-200'
                      : 'border-orange-300 bg-white'
                  }`}
                >
                  <motion.div
                    layout
                    className="absolute top-1 h-5 w-5 rounded-full border-4 border-orange-500 bg-white"
                    style={{
                      left: q.required ? '36px' : '4px'
                    }}
                  />
                </button>
              </div>

              {/* CONDITIONAL */}
              <div className="flex items-center gap-4">
                <span className="font-bold text-orange-700">
                  Conditional
                </span>

                <button
                  onClick={() =>
                    updateQuestion(q.id, {
                      condition: q.condition
                        ? null
                        : { questionId: '', optionId: '', mode: 'same_element' }
                    })
                  }
                  className={`relative h-9 w-20 rounded-full border-4 transition ${
                    q.condition
                      ? 'border-orange-500 bg-orange-200'
                      : 'border-orange-300 bg-white'
                  }`}
                >
                  <motion.div
                    layout
                    className="absolute top-1 h-5 w-5 rounded-full border-4 border-orange-500 bg-white"
                    style={{
                      left: q.condition ? '36px' : '4px'
                    }}
                  />
                </button>
              </div>
            </div>


            {/* CONDITIONAL SELECTORS */}
            {q.condition && (
              <div className="mb-6 flex flex-col gap-4 rounded-2xl border-4 border-orange-200 p-4">
                <select
                  value={q.condition.questionId}
                  onChange={e =>
                    updateQuestion(q.id, {
                      condition: {
                        ...q.condition,
                        questionId: e.target.value,
                        optionId: ''
                      }
                    })
                  }
                  className="rounded-xl border-4 border-orange-200 p-2"
                >
                  <option value="">
                    Select Question
                  </option>
                  {conditionalParents.map(parent => (
                    <option key={parent.id} value={parent.id}>
                      {parent.title}
                    </option>
                  ))}
                </select>

                {q.condition.questionId && (
                  <select
                    value={q.condition.optionId}
                    onChange={e =>
                      updateQuestion(q.id, {
                        condition: {
                          ...q.condition,
                          optionId: e.target.value
                        }
                      })
                    }
                    className="rounded-xl border-4 border-orange-200 p-2"
                  >
                    <option value="">
                      Select Option
                    </option>
                    {questions
                      .find(x => x.id === q.condition.questionId)
                      ?.options.map(o => (
                        <option key={o.id} value={o.id}>
                          {o.label}
                        </option>
                      ))}
                  </select>
                )}
              </div>
            )}

            {/* SIZE (both types) */}
            <div className="mb-6 flex flex-wrap items-center gap-4">
              <span className="font-bold text-orange-700">
                Text Size
              </span>

              <button
                onClick={() => updateQuestion(q.id, { size: 'small' })}
                className={`flex items-center gap-2 rounded-xl border-4 px-4 py-1 font-bold ${
                  size === 'small'
                    ? 'border-orange-500 text-orange-600'
                    : 'border-orange-200 text-orange-700'
                }`}
              >
                Small
              </button>

              <button
                onClick={() => updateQuestion(q.id, { size: 'regular' })}
                className={`rounded-xl border-4 px-4 py-1 font-bold ${
                  size === 'regular'
                    ? 'border-orange-500 text-orange-600'
                    : 'border-orange-200 text-orange-700'
                }`}
              >
                Regular
              </button>
            </div>

            {/* TYPE */}
            <div className="mb-6 flex flex-wrap items-center gap-4">
              <span className="font-bold text-orange-700">
                Answer Type
              </span>
              <button
                onClick={() => updateQuestion(q.id, { type: 'vote' })}
                className={`rounded-xl border-4 px-4 py-1 font-bold ${
                  q.type === 'vote'
                    ? 'border-orange-500 text-orange-600'
                    : 'border-orange-200'
                }`}
              >
                Abstimmung
              </button>

              <button
                onClick={() => updateQuestion(q.id, { type: 'text' })}
                className={`flex items-center gap-2 rounded-xl border-4 px-4 py-1 font-bold ${
                  q.type === 'text'
                    ? 'border-orange-500 text-orange-600'
                    : 'border-orange-200'
                }`}
              >
                Kommentarfeld
              </button>
            </div>

            {/* LAYOUT (vote only) */}
            {q.type === 'vote' && (
              <div className="mb-6 flex flex-wrap items-center gap-4">
                <span className="font-bold text-orange-700">
                  Vote Layout
                </span>

                <button
                  onClick={() => updateQuestion(q.id, { layout: 'inline' })}
                  className={`flex items-center gap-2 rounded-xl border-4 px-4 py-1 font-bold ${
                    voteLayout === 'inline'
                      ? 'border-orange-500 text-orange-600'
                      : 'border-orange-200 text-orange-700'
                  }`}
                >
                  <Rows size={16} />
                  Inline
                </button>

                <button
                  onClick={() => updateQuestion(q.id, { layout: 'stack' })}
                  className={`flex items-center gap-2 rounded-xl border-4 px-4 py-1 font-bold ${
                    voteLayout === 'stack'
                      ? 'border-orange-500 text-orange-600'
                      : 'border-orange-200 text-orange-700'
                  }`}
                >
                  <LayoutList size={16} />
                  Stack
                </button>
              </div>
            )}


            {/* OPTIONS */}
            {q.type === 'vote' &&
              q.options.map(o => (
                <div key={o.id} className="mb-4 flex items-center gap-3">
                  <button
                    onClick={() =>
                      updateQuestion(q.id, {
                        options: q.options.map(opt =>
                          opt.id === o.id
                            ? { ...opt, icon: getNextIcon(opt.icon) }
                            : opt
                        )
                      })
                    }
                    className="flex h-10 w-10 items-center justify-center rounded-xl border-4 border-orange-300"
                  >
                    {renderIcon(o.icon)}
                  </button>

                  <input
                    value={o.label}
                    onChange={e => updateOption(q.id, o.id, e.target.value)}
                    className="flex-1 rounded-xl border-4 border-orange-200 px-3 py-2"
                  />

                  <button
                    onClick={() => deleteOption(q.id, o.id)}
                    className="flex h-10 w-10 items-center justify-center rounded-xl border-4 border-red-300 bg-red-50 text-orange-600"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}

            {q.type === 'vote' && (
              <button
                onClick={() => addOption(q.id)}
                className="rounded-xl border-4 border-orange-400 px-4 py-1 font-bold text-black"
              >
                Add Option
              </button>
            )}

            <div className="mt-8">
              <button
                onClick={() => deleteQuestion(q.id)}
                className="rounded-xl border-2 border-red-500 bg-red-50 px-4 py-1 font-bold text-red-600 inline"
              >
                <Trash2 size={16} className="inline -mt-1"/> Delete Question
              </button>
            </div>
          </motion.div>
        )
      })}
    </div>
  )
}
